import { AbstractControl, AsyncValidatorFn, FormArray, ValidationErrors, Validators } from '@angular/forms';
import { catchError, combineLatest, first, map, Observable, of, startWith } from 'rxjs';
import { BOSCH_EMAIL_PATTERN, EMAIL_PATTERN } from '../constants';
import { AddressModel, CarrierModel } from '../models';

export function makeControlRequiredByKeyValidator(key: string): (formControl: AbstractControl) => ValidationErrors {
  return (formControl: AbstractControl): ValidationErrors => {
    if (!formControl.parent) {
      return null;
    }

    const targetControl = formControl.parent.get(key);

    if (formControl.value) {
      targetControl.setValidators(Validators.required);

      if (!targetControl.value) {
        targetControl.setErrors({ required: true });
      }
    } else {
      targetControl.clearValidators();
      targetControl.setErrors(null);
      targetControl.reset();
    }

    return null;
  };
}
export function mustNotBeBlankValidator(formControl: AbstractControl): { [key: string]: boolean } | null {
  return formControl.value?.length && !formControl.value?.trim()?.length ? { mustNotBeBlank: true } : null;
}

export function boschEmailValidator(formControl: AbstractControl): { [key: string]: boolean } | null {
  return formControl.value?.length && !formControl.value.every((email: string) => BOSCH_EMAIL_PATTERN.test(email))
    ? { boschEmail: true }
    : null;
}

export function checkPartnerEmailPatternInList(formControl: AbstractControl): ValidationErrors | null {
  return formControl.value?.length && !formControl.value.every((email: string) => EMAIL_PATTERN.test(email))
    ? { email: true }
    : null;
}

export function invalidUntilTouchedValidator(formControl: AbstractControl): ValidationErrors | null {
  return formControl.pristine ? { required: true } : null;
}

export function fromGreaterThenToValidator(control: AbstractControl): { [key: string]: boolean } | null {
  const fromControl = control.get('from');
  const toControl = control.get('to');
  if (fromControl.value > toControl.value) {
    return { fromGreaterThenTo: true };
  } else {
    return null;
  }
}

export function oneFromTheListRequiredValidator(control: FormArray): ValidationErrors | null {
  const isValid = control.controls.some((checkbox: AbstractControl<boolean>) => checkbox.value);

  return isValid ? null : { required: true };
}

export function duplicateAddressValidator(formControl: AbstractControl): ValidationErrors | null {
  const addresses = formControl.value;
  if (!addresses || !Array.isArray(addresses)) {
    return null;
  }

  const addressIds = addresses.map((address: AddressModel) =>
    address.fullAddress.substring(0, address.fullAddress.indexOf('|')),
  );
  const uniqueAddressIds = new Set(addressIds);
  if (addressIds.length !== uniqueAddressIds.size) {
    return { duplicateAddress: true };
  }
  return null;
}

export function isCarrierSelectedAsyncValidator(
  items$: Observable<CarrierModel[]>,
  extIdControlName: string,
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value || !control?.parent) {
      return of(null);
    }

    const parent = control.parent;
    const extIdControl = parent.get(extIdControlName);

    if (!extIdControl) {
      return of(null);
    }

    return combineLatest([items$, extIdControl.valueChanges.pipe(startWith(extIdControl.value))]).pipe(
      map(([carriers, extId]: [CarrierModel[], string]) => {
        const carrierName = control.value;

        const isValid = carriers.some((item: CarrierModel) => {
          return item.name === carrierName && (item.extId === extId || item.number === extId);
        });

        return isValid ? null : { noOptionSelected: true };
      }),
      catchError(() => of(null)),
      first(),
    );
  };
}
